/**
 * Apollo.io B2B contact finder
 *
 * Injects a floating popup button and a LinkedIn integration root. Its ids embed the
 * extension version (zp-16-1-0-...), so the probe matches on the stable class names
 * rather than the whole id.
 *
 * Apollo is unusually SLOW to inject — around 10-20 seconds after page load, against
 * well under a second for every other behavioural detector here. Its probes therefore
 * use a long MutationObserver timeout, which makes them the slowest in the set: when
 * Apollo is absent, these two probes alone dominate the report's wall-clock time.
 * That is a deliberate trade of speed for not missing it.
 *
 * Worth noting separately: Apollo randomises its web-accessible resource filenames,
 * and this is CONFIRMED to rotate between releases rather than being a one-off. Comparing
 * v15.1.0 against v16.1.0 (both fetched from Google's own CDN and verified via the CRX3
 * signature — identical publisher key, so both are genuinely Apollo's):
 *
 *   v15.1.0  w4h8b_rNdM_panelnjq2x.html      a28kq_rNdM_linkedin-sidebarvnto6.html
 *   v16.1.0  hrvpe_rNdM_paneljusa7.html      u4vri_rNdM_linkedin-sidebar92gvb.html
 *
 * Same semantic middle, entirely different random prefix and suffix. Every one of the 21
 * resources behaves this way. A resource probe pinned to any of these paths would work
 * until Apollo's next release and then silently stop — which is why this module uses DOM
 * signals despite the manifest exposing 21 resources to <all_urls>.
 *
 * This is a third anti-enumeration technique, distinct from `use_dynamic_url` (Chrome
 * rotates the URL token per session) and `matches: []` (reachable by nobody). Here the
 * resource genuinely is exposed to every origin — you simply cannot guess its name, and
 * any name you learn expires at the next release.
 *
 * Observed on version 16.1.0.
 */
import { probeSelector } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';
const ID = 'alhgpfoeiimagjlnfekdhkjlkiomcapa';
export const apolloIo: ExtensionDefinition = {
  id: ID,
  name: 'Apollo.io: Free B2B Phone Number & Email Finder',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 1_000_000,
  requiresRealPage: true,
  notes:
    'A sales-prospecting tool, so detecting it identifies the visitor as a salesperson ' +
    'rather than a customer. Injects slowly (10-20s), so its probes are the slowest here; ' +
    'it also randomises its resource filenames, which is why detection is DOM-based.',
  signals: [
    { id: 'dom-apollo-root', kind: 'dom',
      description: 'Look for the Apollo injection roots (.apolloio-css-vars-reset, [id*="apollo_everywhere"]).',
      weight: 1, run: () => probeSelector('.apolloio-css-vars-reset, [id*="apollo_everywhere"]', { timeoutMs: 12000 }) },
    { id: 'dom-zp-popup-button', kind: 'dom',
      description: 'Look for .zp-open-popup-button — the floating action button it adds to pages.',
      weight: 0.5, run: () => probeSelector('.zp-open-popup-button', { timeoutMs: 12000 }) },
  ],
};
